import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { fmtIST } from "@/lib/utils";

export default function TeacherHistory() {
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);

  const { data: records = [] } = useQuery({
    queryKey: ["teacher-attendance"],
    queryFn: async () => (await api.get("/teacher/attendance")).data,
  });

  const openDetail = async (rec) => {
    setDetail(rec);
    setDetailData(null);
    try {
      const { data } = await api.get(`/teacher/attendance/${rec.id}/students`);
      setDetailData(data);
    } catch (e) {
      toast.error(errMsg(e));
      setDetail(null);
    }
  };

  return (
    <Layout title="My Submissions">
      <div className="bg-white border border-slate-200 rounded-md" data-testid="teacher-history-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Semester</TableHead>
              <TableHead>Topic taught</TableHead>
              <TableHead className="text-right">Present</TableHead>
              <TableHead className="text-right">Absent</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-10" data-testid="teacher-history-empty">
                You haven't submitted any attendance yet.
              </TableCell></TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id} data-testid={`teacher-record-${r.id}`}>
                <TableCell className="font-data">{r.date}</TableCell>
                <TableCell>{r.period_label}</TableCell>
                <TableCell>{r.subject_name}</TableCell>
                <TableCell>{r.semester_name}</TableCell>
                <TableCell className="max-w-48 truncate text-slate-500" title={r.topic || ""} data-testid={`teacher-topic-${r.id}`}>
                  {r.topic || "—"}
                </TableCell>
                <TableCell className="text-right font-data text-emerald-700">{r.present_count}</TableCell>
                <TableCell className="text-right font-data text-red-700">{r.absent_count}</TableCell>
                <TableCell className="text-slate-500 text-sm">{fmtIST(r.submitted_at)}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" data-testid={`see-details-${r.id}`} onClick={() => openDetail(r)}>
                    <Eye className="h-4 w-4" /> See details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-white rounded-md max-w-2xl" data-testid="teacher-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.subject_name} — {detail?.date} · {detail?.period_label}</DialogTitle>
          </DialogHeader>
          {detailData?.topic ? (
            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="detail-topic">
              <span className="font-medium text-slate-950">Topic taught:</span> {detailData.topic}
            </p>
          ) : null}
          <div className="max-h-96 overflow-auto">
            {!detailData ? (
              <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Name</TableHead><TableHead>Roll Number</TableHead><TableHead>Status</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {detailData.students.map((s) => (
                    <TableRow key={s.student_id} data-testid={`detail-student-${s.student_id}`}>
                      <TableCell>{s.name}</TableCell>
                      <TableCell className="font-data">{s.roll_number}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={s.status === "present"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-red-50 text-red-700 border-red-200"}>
                          {s.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <p className="text-xs text-slate-400">Submitted records can only be edited by the admin.</p>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
